import { useState } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../context/AuthContext';
import { Save, CheckCircle } from 'lucide-react';
import type { CompanySettings } from '../../types/settings';
import { RichTextEditor } from './RichTextEditor';

interface CompanySettingsProps {
  settings: CompanySettings;
  onUpdate: (settings: CompanySettings) => void;
}

export function CompanySettings({ settings, onUpdate }: CompanySettingsProps) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const { user } = useAuth();

  const handleSave = async () => {
    if (!user) return;
    setSaving(true);
    setError('');
    setShowSuccess(false);
    
    try {
      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, { settings });
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000); // Hide success message after 3 seconds
    } catch (err) {
      setError('Failed to save company settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {error && (
        <div className="mb-4 p-4 rounded-md bg-red-50 text-red-700">{error}</div>
      )}

      {showSuccess && (
        <div className="mb-4 p-4 rounded-md bg-green-50 text-green-700 flex items-center gap-2">
          <CheckCircle className="h-5 w-5" />
          Settings saved successfully
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-muted mb-2">Company Name</label>
        <input
          type="text"
          value={settings.companyName}
          onChange={(e) => onUpdate({ ...settings, companyName: e.target.value })}
          className="w-full rounded-md border-[var(--color-border)] bg-[var(--color-bg-card)] text-default input-focus p-2"
          placeholder="Enter your company name"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-muted mb-2">Application Name</label>
        <input
          type="text"
          value={settings.applicationName}
          onChange={(e) => onUpdate({ ...settings, applicationName: e.target.value })}
          className="w-full rounded-md border-[var(--color-border)] bg-[var(--color-bg-card)] text-default input-focus p-2"
          placeholder="Enter application name"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-muted mb-2">Application Tagline</label>
        <RichTextEditor
          content={settings.applicationTagline}
          onChange={(html) => onUpdate({ ...settings, applicationTagline: html })}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-muted mb-2">Logo Link URL</label>
        <input
          type="url"
          value={settings.logoUrl || ''}
          onChange={(e) => onUpdate({ ...settings, logoUrl: e.target.value })}
          className="w-full rounded-md border-[var(--color-border)] bg-[var(--color-bg-card)] text-default input-focus p-2"
          placeholder="Enter URL where logo should link to (e.g., your website)"
        />
        <p className="mt-1 text-sm text-muted">
          When users click your logo, they will be taken to this URL
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-muted mb-2">Light Mode Logo URL</label>
        <input
          type="url"
          value={settings.logoLightMode || ''}
          onChange={(e) => onUpdate({ ...settings, logoLightMode: e.target.value })}
          className="w-full rounded-md border-[var(--color-border)] bg-[var(--color-bg-card)] text-default input-focus p-2"
          placeholder="Enter light mode logo URL"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-muted mb-2">Dark Mode Logo URL</label>
        <input
          type="url"
          value={settings.logoDarkMode || ''}
          onChange={(e) => onUpdate({ ...settings, logoDarkMode: e.target.value })}
          className="w-full rounded-md border-[var(--color-border)] bg-[var(--color-bg-card)] text-default input-focus p-2"
          placeholder="Enter dark mode logo URL"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-muted mb-2">Copyright Text</label>
        <input
          type="text"
          value={settings.copyrightText}
          onChange={(e) => onUpdate({ ...settings, copyrightText: e.target.value })}
          className="w-full rounded-md border-[var(--color-border)] bg-[var(--color-bg-card)] text-default input-focus p-2"
          placeholder="Enter copyright text"
        />
      </div>

      <div className="flex justify-end pt-6">
        <button
          onClick={handleSave}
          disabled={saving}
          className="btn-primary"
        >
          <Save className="h-4 w-4 mr-2" />
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>
    </div>
  );
}